/* eslint-disable parog-web/no-glint-expect-error-directive */
import type ApplicationInstance from '@ember/application/instance';
import type Owner from '@ember/owner';
import { getOwner } from '@ember/owner';
import { trackedArray } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import LinkToForc from 'parog-web/components/link-to-forc';
import type JournalEventModel from 'parog-web/models/journal-event';
import type Patient from 'parog-web/models/patient';
import alertingJeStyle from 'parog-web/modifiers/alerting-je-style';
import ElementRef from 'parog-web/utils/element-ref';
import { getJesOfClass } from 'parog-web/utils/journal-events';
import { sortBy } from 'parog-web/utils/sort';
import type JesHistoryTableComponent from '../jes-history-table';

interface Args {
  table: JesHistoryTableComponent;
  propertyName: string;
  feature: string;
  component: string;
  onInit: (context: JesHistoryTableColumnComponent) => void;
  jeClass: { new (): JournalEventModel };
  isTimePopupDisabled: boolean;
  modelName: string;
  shouldHideArchiveButtons?: boolean;
  customDeleteMessage?: string;
}

interface ColumnValue {
  element: HTMLElement;
  value?: JournalEventModel;
  je?: JournalEventModel;
}

export default class JesHistoryTableColumnComponent extends Component<Args> {
  @tracked edits = trackedArray<ColumnValue>();
  @tracked values: ColumnValue[] = [];

  labelCell = new ElementRef<HTMLTableCellElement>();

  get fullComponentId() {
    return Boolean(this.args.feature) && Boolean(this.args.component)
      ? `${this.args.feature}/${this.args.component}`
      : '';
  }

  public get isTimePopupDisabled() {
    return this.args.isTimePopupDisabled;
  }

  public get jes() {
    if (!this.patient) return [];

    try {
      return (
        this.patient
          .get(this.#patientRelationName)
          // This filter should probably be done elsewhere, maybe on the patient as we have `journalEvents` & `activeJournalEvents` or we can consider that all fetched JE are not archived ?
          .filter(je => !je.isArchived)
          .sort(sortBy('occurredAt', 'desc'))
      );
    } catch {
      return getJesOfClass(this.patient.activeJournalEvents, this.#jeClass);
    }
  }

  get patient() {
    return this.args.table.patient;
  }

  #values: ColumnValue[] = [];

  get #jeClass() {
    return (
      this.args.jeClass ||
      (getOwner(this) as ApplicationInstance).factoryFor(
        `model:${this.args.modelName}`
      )?.class
    );
  }

  get #patientRelationName(): keyof Patient {
    // Transform `weight-measured-journal-event` to `weightMeasuredJournalEvents`
    return `${this.args.modelName.replace(/-./g, m =>
      m[1].toUpperCase()
    )}s` as keyof Patient;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    this.args.onInit(this);
  }

  public addEdit = modifier(
    (element: ColumnValue['element'], [je]: [ColumnValue['value']]) => {
      runTask(this, () => {
        this.edits.push({ element, je });
      });
    }
  );

  public addValue = modifier(
    (element: ColumnValue['element'], [value]: [ColumnValue['value']]) => {
      this.#values.push({ element, value });
    }
  );

  /** Modifier to update the tracked this.values in the next runloop */
  public setupValues = () => {
    runTask(this, () => {
      this.values = this.#values;
    });
  };

  <template>
    {{! @glint-expect-error }}
    {{#unless @hideEditLinks}}
      {{#if @table.dropdownMenu.element}}
        {{#in-element @table.dropdownMenu.element insertBefore=null}}
          <LinkToForc
            @feature={{@feature}}
            @component={{@component}}
            @patient={{this.patient}}
            class='item'
          >

            {{! @glint-expect-error }}
            {{yield to='label'}}
          </LinkToForc>
        {{/in-element}}
      {{/if}}
    {{/unless}}

    {{#if this.labelCell.element}}
      {{#in-element this.labelCell.element}}

        {{! @glint-expect-error }}
        {{yield to='label'}}
      {{/in-element}}
    {{/if}}

    {{#each this.values as |hash|}}
      {{#in-element hash.element}}
        <span {{alertingJeStyle hash.value}}>

          {{! @glint-expect-error }}
          {{yield hash.value to='value'}}
        </span>
      {{/in-element}}
    {{/each}}

    {{! @glint-expect-error }}
    {{#unless @hideEditLinks}}
      {{#each this.edits as |hash|}}
        {{#if (can 'edit form' this.fullComponentId)}}
          {{#in-element hash.element}}
            <LinkToForc
              @feature={{@feature}}
              @component={{@component}}
              @patient={{this.patient}}
              @sourceJeId={{hash.je.id}}
              data-test-edit-record-link
            >
              <i
                class='pencil alternate primary icon [.show-shyone-on-hover:hover_&]:![opacity:1] [@media(hover:hover)]:![opacity:0] [@media(hover:hover)]:[transition:all_100ms_ease-in-out]'
              ></i>
            </LinkToForc>
          {{/in-element}}
        {{/if}}
      {{/each}}
    {{/unless}}
  </template>
}
